import { KeyboardEventHandler } from 'react';

import { RegExStrings } from '../../lib/util/DataHelpers';

const DATE_INPUT_DIGIT_LIMIT = 8;
const DATE_INPUT_MAX_LENGTH_WITH_SEPARATORS = 10;
const ALLOWED_STATIC_KEYS = new Set([
  'Backspace',
  'Delete',
  'Tab',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
  '/',
]);

const sanitizeToDigits = (value: string): string =>
  (value || '').replace(RegExStrings.digitsOnly, '').slice(0, DATE_INPUT_DIGIT_LIMIT);

const formatDigitsAsDate = (digits: string): string => {
  if (digits.length > 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  }
  if (digits.length > 2) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }
  return digits;
};

export const getFormattedDateInputValue = (value: string): string => {
  const sanitizedDigits = sanitizeToDigits(value);
  return formatDigitsAsDate(sanitizedDigits).slice(0, DATE_INPUT_MAX_LENGTH_WITH_SEPARATORS);
};

const hasReachedMaxSeparators = (value: string): boolean =>
  ((value.match(RegExStrings.forwardSlash)?.length ?? 0) >= 2);

export const createDateInputKeyDownHandler = (
  getCurrentValue: () => string,
): KeyboardEventHandler<HTMLInputElement> => (event) => {
  const { key } = event;
  const isNumericKey = key >= '0' && key <= '9';
  const isAllowedKey = isNumericKey || ALLOWED_STATIC_KEYS.has(key);

  if (!isAllowedKey) {
    event.preventDefault();
    return;
  }

  if (key === '/' && hasReachedMaxSeparators(getCurrentValue())) {
    event.preventDefault();
  }
};

export const createCalendarIconKeyDownHandler = (
  openCalendar: () => void,
): KeyboardEventHandler<HTMLSpanElement> => (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    openCalendar();
  }
};
